import { useMutation } from '@tanstack/react-query'
import { useAuth } from './useAuth'
import { AttachmentRequest } from '../types/types'

export const MAX_ATTACHMENT_SIZE_BYTES = 20 * 1024 * 1024 // 20 MB

interface UploadAttachmentVariables {
  requisitionId: string
  resultId: string
  file: File
}

export const useUploadAttachmentMutation = () => {
  const { authFetch } = useAuth()
  return useMutation({
    mutationFn: async ({
      requisitionId,
      resultId,
      file
    }: UploadAttachmentVariables) => {
      const isPdfByName = file.name.toLowerCase().endsWith('.pdf')
      const inferredType =
        file.type ||
        (isPdfByName ? 'application/pdf' : 'application/octet-stream')
      const body: AttachmentRequest = {
        fileName: file.name,
        mediaType: inferredType,
        fileSize: file.size
      }
      const { data, error } = await authFetch().POST(
        '/requisitions/{requisitionId}/results/{resultId}/attachments',
        {
          params: {
            path: {
              requisitionId: Number(requisitionId),
              resultId: Number(resultId)
            }
          },
          body
        }
      )
      if (error) {
        const msg =
          error?.detail ||
          error?.title ||
          'Kunne ikke opprette opplastingslenke'
        throw new Error(msg)
      }
      if (!data?.signedUrl) {
        throw new Error('Mangler signert opplastings-URL')
      }
      const uploadResp = await fetch(data.signedUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': body.mediaType
        },
        body: file
      })
      if (!uploadResp.ok) {
        let message = `Opplasting feilet: ${uploadResp.status} ${uploadResp.statusText}`
        try {
          const text = await uploadResp.text()
          if (text) message = `${message} - ${text}`
        } catch {
          // ignorer parse-feil
        }
        throw new Error(message)
      }
      return true
    }
  })
}
